import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    CheckCircle2,
    AlertTriangle,
    ListChecks,
    Loader2,
    ShieldCheck,
    MailCheck,
    Search,
    FileCheck,
    ArrowRight,
} from "lucide-react";
import { Card } from "../components/ui/card";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { submitAuditRequest } from "../lib/api";

const SERVICES = [
    "Web Audit", "Mobile App Audit", "PDF Remediation",
    "Office Document Remediation", "Design Audit", "VPAT/ACR", "Other",
];
const INDUSTRIES = [
    "E-commerce", "Healthcare", "Banking & Finance", "Education",
    "Government", "SaaS", "Publishing & Media", "Legal & Insurance", "Other",
];
const STANDARDS = [
    "WCAG 2.1 / 2.2", "ADA", "EAA", "Section 508", "AODA",
    "BITV 2.0", "RGAA", "PDF/UA", "Indian RPWD/GIGW", "Not sure",
];

const FAQS = [
    { q: "Is this really free?", a: "Yes — no credit card, no obligation, no fine print. We deliver a 1-page snapshot so you can see the value of working with us before any commercial conversation." },
    { q: "How detailed is the report?", a: "It's a 1-page executive summary covering the top 5 critical issues, their severity, and recommended next steps. Comprehensive paid audits go significantly deeper across every WCAG criterion." },
    { q: "What if my site has issues?", a: "We'll explain them clearly, in plain language. Whether you choose to hire us for the fix is entirely your decision — there's no upsell pressure." },
    { q: "Can I request audits for documents (PDFs)?", a: "Yes — describe the document type in the form and we'll evaluate a representative sample. PDF remediation is one of our core services." },
    { q: "Do you store my data?", a: "We store the information you submit so we can respond. We never share or sell it. Read our Privacy Policy for full details." },
];

/* ---------------- Reusable form pieces ---------------- */
const Field = ({ label, htmlFor, required, error, children, hint }) => (
    <div>
        <label htmlFor={htmlFor} className="block text-sm font-semibold text-brand-text mb-1.5">
            {label} {required && <span className="text-brand-error" aria-hidden="true">*</span>}
            {required && <span className="sr-only">(required)</span>}
        </label>
        {children}
        {hint && !error && <p className="mt-1 text-xs text-brand-muted">{hint}</p>}
        {error && (
            <p
                role="alert"
                id={`${htmlFor}-error`}
                className="mt-1 text-xs font-medium text-brand-error flex items-center gap-1"
            >
                <AlertTriangle size={12} aria-hidden="true" /> {error}
            </p>
        )}
    </div>
);

const inputCls =
    "w-full min-h-[48px] px-4 rounded-md border border-brand-border bg-white text-brand-text placeholder:text-brand-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tealBright focus-visible:border-brand-tealBright transition-colors";

/* ---------------- HERO ---------------- */
const Hero = () => (
    <section className="relative overflow-hidden border-b border-brand-border" aria-labelledby="audit-hero">
        <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-white to-[#F1F7F8]" aria-hidden="true" />
        <div className="absolute -top-32 -right-24 w-[420px] h-[420px] rounded-full bg-[#127A75]/10 blur-3xl -z-10" aria-hidden="true" />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Free Mini Audit</p>
            <h1 id="audit-hero" className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-bold text-brand-text tracking-tight max-w-4xl">
                Free Accessibility Snapshot. Real Insights. No Obligation.
            </h1>
            <p className="mt-5 text-lg text-brand-body max-w-3xl">
                Find out where your website or document stands on WCAG compliance — delivered within 48 hours.
            </p>
            <ul className="mt-7 flex flex-wrap gap-x-6 gap-y-3 text-sm font-semibold text-brand-text">
                {[
                    "No credit card required",
                    "48-hour delivery",
                    "Real expert review",
                ].map((t) => (
                    <li key={t} className="flex items-center gap-2">
                        <CheckCircle2 size={18} className="text-brand-teal" aria-hidden="true" /> {t}
                    </li>
                ))}
            </ul>
        </div>
    </section>
);

/* ---------------- WHAT YOU GET ---------------- */
const WhatYouGet = () => {
    const items = [
        { icon: AlertTriangle, title: "Top 5 Critical Issues", desc: "The most impactful accessibility problems on your site or document." },
        { icon: ListChecks, title: "Severity Assessment", desc: "Each issue rated low / medium / high / critical with business risk." },
        { icon: ArrowRight, title: "Recommended Next Steps", desc: "Prioritized action plan, free of jargon." },
    ];
    return (
        <section aria-labelledby="audit-what" className="py-16">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <h2 id="audit-what" className="sr-only">What you get</h2>
                <ul className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {items.map(({ icon: Icon, title, desc }) => (
                        <li key={title}>
                            <Card className="p-7 h-full border-brand-border" data-testid={`audit-benefit-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                                <div className="w-11 h-11 rounded-md bg-brand-soft text-brand-teal inline-flex items-center justify-center">
                                    <Icon size={22} aria-hidden="true" />
                                </div>
                                <h3 className="mt-5 text-lg font-bold text-brand-text">{title}</h3>
                                <p className="mt-2 text-sm text-brand-body leading-relaxed">{desc}</p>
                            </Card>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
};

/* ---------------- FORM ---------------- */
const initial = {
    name: "", email: "", company: "", target: "",
    service: "", industry: "", standards: [],
    description: "", consent: false, website: "",
};

const validate = (v) => {
    const e = {};
    if (!v.name.trim()) e.name = "Please enter your name.";
    if (!v.email.trim()) e.email = "Please enter your email.";
    else if (!/^\S+@\S+\.\S+$/.test(v.email)) e.email = "Please enter a valid email.";
    if (!v.company.trim()) e.company = "Please enter your company name.";
    if (!v.target.trim()) e.target = "Tell us what you'd like reviewed.";
    if (!v.service) e.service = "Choose the service you need.";
    if (!v.industry) e.industry = "Choose your industry.";
    if (v.description && v.description.length > 500) e.description = "Please keep it under 500 characters.";
    if (!v.consent) e.consent = "Consent is required to submit.";
    return e;
};

const AuditForm = () => {
    const [v, setV] = useState(initial);
    const [errors, setErrors] = useState({});
    const [submitState, setSubmitState] = useState("idle");
    const [serverErr, setServerErr] = useState("");

    const set = (k) => (e) =>
        setV((p) => ({ ...p, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const toggleStd = (label) =>
        setV((p) => ({
            ...p,
            standards: p.standards.includes(label)
                ? p.standards.filter((s) => s !== label)
                : [...p.standards, label],
        }));

    const isValid = Object.keys(validate(v)).length === 0;

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = validate(v);
        setErrors(errs);
        if (Object.keys(errs).length > 0) {
            const firstId = Object.keys(errs)[0];
            document.getElementById(firstId)?.focus();
            return;
        }
        setSubmitState("loading");
        setServerErr("");
        try {
            await submitAuditRequest(v);
            setSubmitState("success");
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setServerErr(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
            setSubmitState("idle");
        }
    };

    if (submitState === "success") {
        return (
            <section aria-labelledby="success-heading" className="py-20">
                <div className="mx-auto max-w-2xl px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D1FAE5] text-[#065F46] mb-6" aria-hidden="true">
                        <CheckCircle2 size={32} />
                    </div>
                    <h2 id="success-heading" className="text-3xl sm:text-4xl font-bold text-brand-text">
                        Request received. Thank you.
                    </h2>
                    <p className="mt-4 text-base text-brand-body">
                        We'll confirm via email within 1 hour and deliver your free 1-page snapshot within 48 hours.
                        Check your inbox (and spam folder) for a note from <strong>hello@accessnova.co</strong>.
                    </p>
                    <Link
                        to="/"
                        className="mt-8 inline-flex items-center gap-2 rounded-md bg-brand-navy text-white font-semibold px-6 py-3 hover:bg-brand-teal transition-colors touch-target"
                        data-testid="audit-success-home"
                    >
                        Back to homepage
                    </Link>
                </div>
            </section>
        );
    }

    return (
        <section aria-labelledby="audit-form-heading" className="bg-brand-soft border-y border-brand-border py-16 lg:py-20">
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                <h2 id="audit-form-heading" className="text-3xl sm:text-4xl font-bold text-brand-text">
                    Request your free audit
                </h2>
                <p className="mt-3 text-base text-brand-body">
                    Required fields are marked <span className="text-brand-error" aria-hidden="true">*</span>. We respond within 1 hour during business days.
                </p>

                {serverErr && (
                    <div role="alert" aria-live="assertive"
                         className="mt-6 rounded-md border border-[#FECACA] bg-[#FEF2F2] text-[#991B1B] p-4 text-sm font-medium"
                         data-testid="audit-server-error">
                        {serverErr}
                    </div>
                )}

                <form onSubmit={onSubmit} noValidate className="mt-8 grid grid-cols-1 gap-5" data-testid="audit-form">
                    {/* Honeypot — visually hidden, not announced to AT */}
                    <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
                        <label>Website (do not fill)
                            <input type="text" tabIndex="-1" autoComplete="off" value={v.website} onChange={set("website")} />
                        </label>
                    </div>

                    <Field label="Full Name" htmlFor="name" required error={errors.name}>
                        <input id="name" type="text" autoComplete="name" required
                               value={v.name} onChange={set("name")} className={inputCls}
                               aria-invalid={!!errors.name} aria-describedby={errors.name ? "name-error" : undefined}
                               data-testid="audit-input-name" />
                    </Field>

                    <Field label="Business Email" htmlFor="email" required error={errors.email}>
                        <input id="email" type="email" autoComplete="email" required
                               value={v.email} onChange={set("email")} className={inputCls}
                               aria-invalid={!!errors.email} aria-describedby={errors.email ? "email-error" : undefined}
                               data-testid="audit-input-email" />
                    </Field>

                    <Field label="Company Name" htmlFor="company" required error={errors.company}>
                        <input id="company" type="text" autoComplete="organization" required
                               value={v.company} onChange={set("company")} className={inputCls}
                               aria-invalid={!!errors.company} aria-describedby={errors.company ? "company-error" : undefined}
                               data-testid="audit-input-company" />
                    </Field>

                    <Field label="Website URL or Document Type" htmlFor="target" required error={errors.target}
                           hint={'A URL like https://example.com or a description like "PDF healthcare forms".'}>
                        <input id="target" type="text" required
                               value={v.target} onChange={set("target")} className={inputCls}
                               aria-invalid={!!errors.target} aria-describedby={errors.target ? "target-error" : undefined}
                               data-testid="audit-input-target" />
                    </Field>

                    <Field label="Service Needed" htmlFor="service" required error={errors.service}>
                        <select id="service" required value={v.service} onChange={set("service")}
                                className={inputCls}
                                aria-invalid={!!errors.service} aria-describedby={errors.service ? "service-error" : undefined}
                                data-testid="audit-input-service">
                            <option value="">Select a service…</option>
                            {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>

                    <Field label="Industry" htmlFor="industry" required error={errors.industry}>
                        <select id="industry" required value={v.industry} onChange={set("industry")}
                                className={inputCls}
                                aria-invalid={!!errors.industry} aria-describedby={errors.industry ? "industry-error" : undefined}
                                data-testid="audit-input-industry">
                            <option value="">Select an industry…</option>
                            {INDUSTRIES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>

                    <fieldset className="border-0 p-0 m-0">
                        <legend className="block text-sm font-semibold text-brand-text mb-2">
                            Compliance Standards Concern <span className="text-brand-muted font-normal">(select all that apply)</span>
                        </legend>
                        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {STANDARDS.map((s) => {
                                const checked = v.standards.includes(s);
                                return (
                                    <li key={s}>
                                        <label className={`flex items-center gap-3 px-4 py-3 rounded-md border cursor-pointer transition-colors min-h-[48px] ${
                                            checked ? "bg-white border-brand-teal" : "bg-white border-brand-border hover:border-brand-teal/50"
                                        }`}>
                                            <input type="checkbox" checked={checked}
                                                   onChange={() => toggleStd(s)}
                                                   className="w-4 h-4 accent-[#127A75]"
                                                   data-testid={`audit-std-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
                                            <span className="text-sm text-brand-text font-medium">{s}</span>
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                    </fieldset>

                    <Field label="Brief description of concerns" htmlFor="description" error={errors.description}
                           hint={`Optional · ${v.description.length}/500 characters`}>
                        <textarea id="description" rows={4} maxLength={500}
                                  value={v.description} onChange={set("description")}
                                  className={`${inputCls} min-h-[112px] py-3 resize-y`}
                                  aria-invalid={!!errors.description}
                                  aria-describedby={errors.description ? "description-error" : undefined}
                                  data-testid="audit-input-description" />
                    </Field>

                    <Field label="" htmlFor="consent" error={errors.consent}>
                        <label className="flex items-start gap-3 text-sm text-brand-body cursor-pointer">
                            <input id="consent" type="checkbox" required checked={v.consent} onChange={set("consent")}
                                   className="mt-1 w-4 h-4 accent-[#127A75]"
                                   aria-invalid={!!errors.consent}
                                   aria-describedby={errors.consent ? "consent-error" : undefined}
                                   data-testid="audit-input-consent" />
                            <span>
                                I agree to be contacted regarding my audit request. AccessNova respects privacy and never shares data.
                                <span className="text-brand-error" aria-hidden="true"> *</span>
                            </span>
                        </label>
                    </Field>

                    <button
                        type="submit"
                        disabled={!isValid || submitState === "loading"}
                        className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-brand-navy text-white font-semibold px-6 py-3 hover:bg-brand-teal transition-colors duration-200 touch-target disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="audit-submit"
                    >
                        {submitState === "loading" ? (
                            <><Loader2 size={18} className="animate-spin" aria-hidden="true" /> Submitting…</>
                        ) : (
                            <>Request Free Audit <ArrowRight size={18} aria-hidden="true" /></>
                        )}
                    </button>

                    <p className="text-xs text-brand-muted">
                        We typically respond within 1 hour. By submitting, you confirm you've read our{" "}
                        <Link to="/privacy" className="text-brand-teal underline hover:text-brand-tealBright">Privacy Policy</Link>.
                    </p>
                </form>
            </div>
        </section>
    );
};

/* ---------------- WHAT HAPPENS NEXT ---------------- */
const WhatHappensNext = () => {
    const steps = [
        { icon: MailCheck, title: "Confirmation", desc: "We receive your request and confirm via email within 1 hour." },
        { icon: Search, title: "Expert Review", desc: "Our team runs comprehensive automated scans and manual checks." },
        { icon: FileCheck, title: "Snapshot Delivered", desc: "You receive your free 1-page snapshot within 48 hours." },
    ];
    return (
        <section aria-labelledby="audit-next" className="py-20">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">What happens next</p>
                <h2 id="audit-next" className="mt-2 text-3xl sm:text-4xl font-bold text-brand-text">Your audit, in three steps.</h2>
                <ol className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {steps.map((s, i) => {
                        const Icon = s.icon;
                        return (
                            <li key={s.title}>
                                <Card className="h-full p-7 border-brand-border bg-white relative">
                                    <span className="absolute -top-4 left-7 inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-navy text-white font-bold text-sm">
                                        {i + 1}
                                    </span>
                                    <Icon className="text-brand-teal mt-4" size={28} aria-hidden="true" />
                                    <h3 className="mt-3 text-lg font-bold text-brand-text">{s.title}</h3>
                                    <p className="mt-2 text-sm text-brand-body leading-relaxed">{s.desc}</p>
                                </Card>
                            </li>
                        );
                    })}
                </ol>
            </div>
        </section>
    );
};

/* ---------------- FAQ ---------------- */
const FAQ = () => (
    <section aria-labelledby="audit-faq" className="bg-brand-soft border-t border-brand-border py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <p className="text-sm font-semibold uppercase tracking-wider text-brand-teal">Common questions</p>
            <h2 id="audit-faq" className="mt-2 text-3xl sm:text-4xl font-bold text-brand-text">Frequently asked.</h2>
            <Accordion type="single" collapsible className="mt-8 bg-white rounded-lg border border-brand-border" data-testid="audit-faq-accordion">
                {FAQS.map((f, i) => (
                    <AccordionItem key={f.q} value={`q${i}`} className="border-b last:border-b-0 border-brand-border px-5">
                        <AccordionTrigger className="text-left font-semibold text-brand-text hover:no-underline py-5">
                            {f.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-brand-body leading-relaxed pb-5">
                            {f.a}
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
            <p className="mt-6 flex items-center gap-2 text-sm text-brand-muted">
                <ShieldCheck size={16} className="text-brand-teal" aria-hidden="true" />
                Your data is private and never shared.
            </p>
        </div>
    </section>
);

/* ---------------- PAGE ---------------- */
export default function FreeAudit() {
    useEffect(() => { document.title = "Free Mini Audit — AccessNova"; }, []);
    return (
        <>
            <Hero />
            <WhatYouGet />
            <AuditForm />
            <WhatHappensNext />
            <FAQ />
        </>
    );
}
